import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Award } from "lucide-react";
import { MaskedEmail } from "@/components/MaskedEmail";
import { WinnerReveal } from "@/components/WinnerReveal";

export default function AdminEntries() {
  const { id } = useParams();
  const [comp, setComp] = useState(null);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(null); // {winner, ticketPool}

  const load = async () => {
    setLoading(true);
    try {
      const [c, e] = await Promise.all([
        api.get(`/admin/competitions/${id}`),
        api.get(`/admin/competitions/${id}/entries`),
      ]);
      setComp(c.data);
      setEntries(e.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [id]);

  const draw = async () => {
    if (!confirm("Run the winner draw now? The live reveal animation will play. This is final.")) return;
    try {
      const { data } = await api.post(`/admin/competitions/${id}/draw`);
      setReveal({ winner: data.winner, ticketPool: entries.map((e) => e.ticket_number) });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const closeReveal = () => {
    setReveal(null);
    load();
  };

  return (
    <div data-testid="admin-entries-page">
      {reveal && (
        <WinnerReveal
          open
          compTitle={comp?.title || ""}
          winner={reveal.winner}
          ticketPool={reveal.ticketPool}
          onClose={closeReveal}
        />
      )}
      <Link to="/admin" className="text-white/60 hover:text-white text-sm inline-flex items-center gap-1 mb-4">
        <ArrowLeft size={14} /> Back
      </Link>

      {loading ? <div className="text-white/50">Loading…</div> : !comp ? <div className="text-white/50">Not found</div> : (
        <>
          <div className="flex items-start justify-between mb-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--ufr-green)] font-bold">{comp.subtitle}</div>
              <h1 className="font-display text-3xl md:text-4xl text-white mt-1">{comp.title}</h1>
              <div className="text-white/60 text-sm mt-1">
                {entries.length} entries · {comp.tickets_sold || 0} / {comp.total_tickets} tickets sold
              </div>
            </div>
            {!comp.winner ? (
              <button onClick={draw} className="btn-ufr-primary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="admin-entries-draw-btn">
                <Award size={14} /> Draw winner
              </button>
            ) : (
              <div className="ufr-chip border-yellow-400/40 bg-yellow-400/5 text-yellow-300 flex-wrap gap-x-2" data-testid="admin-entries-winner-chip">
                <Award size={12} /> Winner: #{comp.winner.ticket_number} {comp.winner.user_name || "—"}
                <MaskedEmail email={comp.winner.user_email} className="text-yellow-300/70 !text-[11px]" testid="admin-entries-winner-email" />
              </div>
            )}
          </div>

          <div className="ufr-card rounded-md overflow-hidden">
            <div className="grid grid-cols-12 px-4 py-3 text-[10px] uppercase tracking-[0.18em] text-white/60 font-bold border-b border-white/5">
              <div className="col-span-2">Ticket #</div>
              <div className="col-span-4">Buyer</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-3">Purchased</div>
            </div>
            {entries.length === 0 ? (
              <div className="px-4 py-10 text-center text-white/50 text-sm">No entries yet.</div>
            ) : entries.map((e) => (
              <div key={e.id} className={`grid grid-cols-12 px-4 py-3 border-b border-white/5 text-sm items-center ${comp.winner?.ticket_number === e.ticket_number ? "bg-yellow-500/5" : ""}`} data-testid={`admin-entry-row-${e.id}`}>
                <div className="col-span-2 font-display text-[color:var(--ufr-green)]">#{e.ticket_number}</div>
                <div className="col-span-4 text-white/90">{e.user_name || "—"}</div>
                <div className="col-span-3 text-white/70"><MaskedEmail email={e.user_email} testid={`admin-entry-email-${e.id}`} /></div>
                <div className="col-span-3 text-white/50 text-xs">{new Date(e.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
